import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

import { User, Company, createUser, getAllCompanies } from '../../services/adminService';

interface CreateUserPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: User) => void;
}

export default function CreateUserPopup({ isOpen, onClose, onUserCreated }: CreateUserPopupProps) {
  const [formData, setFormData] = useState<Partial<User> & { password: string; confirmPassword: string }>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyId: 0,
    role: 'ROLE_USER',
    isActive: true,
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      // Fetch companies for the dropdown
      const fetchCompanies = async () => {
        try {
          const companiesData = await getAllCompanies();
          setCompanies(companiesData);
          
          // Set first company as default if available
          if (companiesData.length > 0 && !formData.companyId) {
            setFormData(prev => ({ ...prev, companyId: companiesData[0].id }));
          }
        } catch (error) {
          console.error('Error fetching companies:', error);
          showErrorToast('Erreur lors du chargement des entreprises', 'companies-load-error');
        }
      };
      
      fetchCompanies();
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    if (!formData.companyId) {
      newErrors.companyId = 'L\'entreprise est requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else if (name === 'companyId') {
      setFormData({
        ...formData,
        [name]: parseInt(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Create a new object without the confirmPassword field
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        companyId: formData.companyId,
        role: formData.role,
        isActive: formData.isActive
      };
      
      const newUser = await createUser(userData);
      onUserCreated(newUser);
      showSuccessToast('Utilisateur créé avec succès', `user-created-${newUser.id}`);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        companyId: companies.length > 0 ? companies[0].id : 0,
        role: 'ROLE_USER',
        isActive: true,
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating user:', error);
      showErrorToast('Erreur lors de la création de l\'utilisateur', 'user-create-error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Créer un nouvel utilisateur
                    </Dialog.Title>
                    <div className="mt-4">
                      <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                          <div className="sm:col-span-3">
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                              Prénom *
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="firstName"
                                id="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.firstName 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              />
                              {errors.firstName && (
                                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                              Nom *
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="lastName"
                                id="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.lastName 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              />
                              {errors.lastName && (
                                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-6">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Adresse email *
                            </label>
                            <div className="mt-1">
                              <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.email 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              />
                              {errors.email && (
                                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-6">
                            <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                              Entreprise *
                            </label>
                            <div className="mt-1">
                              <select
                                id="companyId"
                                name="companyId"
                                value={formData.companyId || ''}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.companyId 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              >
                                <option value="">Sélectionner une entreprise</option>
                                {companies.map((company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.name} ({company.siret})
                                  </option>
                                ))}
                              </select>
                              {errors.companyId && (
                                <p className="mt-1 text-sm text-red-600">{errors.companyId}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                              Mot de passe *
                            </label>
                            <div className="mt-1">
                              <input
                                type="password"
                                name="password"
                                id="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.password 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              />
                              {errors.password && (
                                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                              Confirmer le mot de passe *
                            </label>
                            <div className="mt-1">
                              <input
                                type="password"
                                name="confirmPassword"
                                id="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                                  errors.confirmPassword 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                                }`}
                              />
                              {errors.confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                              Rôle
                            </label>
                            <div className="mt-1">
                              <select
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              >
                                <option value="ROLE_USER">Utilisateur</option>
                                <option value="ROLE_ADMIN">Administrateur</option>
                              </select>
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <div className="flex h-full items-center">
                              <div className="relative flex items-start">
                                <div className="flex h-5 items-center">
                                  <input
                                    id="isActive"
                                    name="isActive"
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                </div>
                                <div className="ml-3 text-sm">
                                  <label htmlFor="isActive" className="font-medium text-gray-700">
                                    Compte actif
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                          >
                            {isSubmitting ? 'Création en cours...' : 'Créer'}
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            onClick={onClose}
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
} 